import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import axios from 'axios';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  TextField,
  InputAdornment,
  Button,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import moment from 'moment';
import SearchIcon from '@material-ui/icons/Search';
import EmojiEventsIcon from '@material-ui/icons/EmojiEvents';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import GetAppIcon from '@material-ui/icons/GetApp';
import CasinoIcon from '@material-ui/icons/Casino';
import HistoryIcon from '@material-ui/icons/History';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';
import { toastr } from 'react-redux-toastr';
import GeneralGrid from 'common/components/layout/grid/GeneralGrid';
import LoadingProgress from 'common/components/progress/loading';
import { BASE_API, USER_TOKEN } from 'config/consts';

const useStyles = makeStyles((theme) => ({
  container: {
    maxWidth: '90vw',
    paddingBottom: '4rem',
  },
  header: {
    marginBottom: theme.spacing(3),
    marginTop: theme.spacing(2),
  },
  title: {
    fontSize: 24,
    fontWeight: 700,
    color: '#1a3a5c',
  },
  subtitle: {
    fontSize: 14,
    color: '#666',
    marginTop: 4,
  },
  filtersCard: {
    borderRadius: 12,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  filtersRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 2fr',
    gap: theme.spacing(2),
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  actionsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  primaryActionBtn: {
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: 8,
    paddingLeft: 20,
    paddingRight: 20,
    height: 40,
    whiteSpace: 'nowrap',
  },
  secondaryActionBtn: {
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: 8,
    paddingLeft: 16,
    paddingRight: 16,
    height: 40,
    whiteSpace: 'nowrap',
  },
  tableCard: {
    borderRadius: 12,
    overflow: 'hidden',
  },
  headCell: {
    fontWeight: 700,
    backgroundColor: '#f5f7fa',
    color: '#1a3a5c',
  },
  personBlock: {
    display: 'flex',
    flexDirection: 'column',
  },
  personName: {
    fontWeight: 600,
    color: '#1a3a5c',
  },
  personMeta: {
    fontSize: 12,
    color: '#666',
  },
  numbersWrap: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4,
  },
  numberChip: {
    backgroundColor: '#eef5ff',
    color: '#1a3a5c',
    fontFamily: 'monospace',
    fontWeight: 600,
  },
  statusChip: {
    fontWeight: 600,
  },
  emptyState: {
    padding: theme.spacing(6),
    textAlign: 'center',
    color: '#777',
  },
  generateBtn: {
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: 8,
    paddingLeft: 20,
    paddingRight: 20,
  },
  searchResultItem: {
    borderBottom: '1px solid #eee',
  },
  numberBadge: {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: 6,
    backgroundColor: '#eef5ff',
    color: '#1a3a5c',
    fontFamily: 'monospace',
    fontWeight: 700,
    marginRight: 6,
    marginBottom: 6,
  },
}));

const statusConfig = {
  pending: { label: 'Pendente', color: '#ff9800' },
  registered: { label: 'Cadastrado', color: '#2196f3' },
  affiliated: { label: 'Filiado', color: '#9c27b0' },
  completed: { label: 'Concluída', color: '#4caf50' },
};

const formatCpf = (cpf) => {
  if (!cpf) return '—';
  const digits = String(cpf).replace(/\D/g, '');
  if (digits.length !== 11) return cpf;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
};

const formatPhone = (phone) => {
  if (!phone) return '—';
  const d = String(phone).replace(/\D/g, '');
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return phone;
};

// Cabeçalho exigido pela SPA (reguladora) para o CSV de números da sorte.
const SPA_HEADERS = [
  'cpf',
  'cnpj',
  'nome',
  'numero_serie',
  'numero_sorteavel',
  'data_hora_participacao',
  'email',
  'telefone',
  'estrangeiro',
];

const csvEscape = (value) => {
  let s = value == null ? '' : String(value);
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const padSeries = (v) => String(v).padStart(2, '0');

const MONTHS_PT = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

// Mês corrente no formato 'YYYY-MM' (mesma chave usada no lucky_numbers_schedule).
const currentYM = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

// Lê o lucky_numbers_schedule da campanha (TEXT/JSON) e devolve a lista
// ordenada [{ month: 'YYYY-MM', quantity }]. Tolera string, array ou nulo.
const parseSchedule = (schedule) => {
  if (!schedule) return [];
  let parsed = schedule;
  if (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed);
    } catch (e) {
      return [];
    }
  }
  if (!Array.isArray(parsed)) return [];
  return parsed
    .filter((e) => e && e.month)
    .map((e) => ({ month: e.month, quantity: parseInt(e.quantity, 10) || 1 }))
    .sort((a, b) => a.month.localeCompare(b.month));
};

const formatMonthLabel = (ym) => {
  const [y, m] = String(ym || '').split('-');
  const name = MONTHS_PT[parseInt(m, 10) - 1];
  return name ? `${name}/${y}` : ym;
};

// Chips com as quantidades configuradas por mês na atividade. Clicar define a
// quantidade. O mês atual fica destacado (é a sugestão padrão).
const MonthQuantityChips = ({ schedule, value, onPick, disabled }) => {
  const options = parseSchedule(schedule);
  if (options.length === 0) return null;
  const cur = currentYM();
  return (
    <Box style={{ marginTop: 8 }}>
      <Typography variant="caption" style={{ display: 'block', color: '#888', marginBottom: 4 }}>
        Quantidades configuradas por mês (clique para usar):
      </Typography>
      <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {options.map((o) => {
          const isCurrent = o.month === cur;
          const isSelected = parseInt(value, 10) === o.quantity;
          return (
            <Chip
              key={o.month}
              size="small"
              clickable={!disabled}
              onClick={() => !disabled && onPick(o.quantity)}
              label={`${formatMonthLabel(o.month)} · ${o.quantity}`}
              style={{
                height: 22,
                fontWeight: isCurrent ? 700 : 400,
                backgroundColor: isSelected ? '#1a3a5c' : isCurrent ? '#e3f2fd' : '#f0f0f0',
                color: isSelected ? '#fff' : '#1a3a5c',
                border: isCurrent && !isSelected ? '1px solid #1a3a5c' : undefined,
              }}
            />
          );
        })}
      </Box>
    </Box>
  );
};

// Quantidade sugerida para o mês atual — espelha getQuantityForCurrentMonth do
// backend: bate o mês exato; se já passou do último mês configurado, usa o
// último; senão 1.
const scheduleQuantityForCurrentMonth = (schedule) => {
  const list = parseSchedule(schedule);
  if (list.length === 0) return 1;
  const cur = currentYM();
  const match = list.find((e) => e.month === cur);
  if (match) return match.quantity;
  const last = list[list.length - 1];
  if (cur > last.month) return last.quantity;
  return 1;
};

// As casas vêm hoje do backend como objetos {name, seriesStart, seriesEnd, prizeIndex}.
// Normalizamos para garantir compatibilidade caso venham como strings (legado).
const normalizeHouses = (houses) => {
  if (!Array.isArray(houses)) return [];
  return houses
    .map((h, idx) => {
      if (typeof h === 'string') {
        return {
          name: h,
          seriesStart: idx * 10,
          seriesEnd: idx * 10 + 9,
          prizeIndex: idx + 1,
        };
      }
      return {
        name: h?.name || '',
        seriesStart: parseInt(h?.seriesStart, 10),
        seriesEnd: parseInt(h?.seriesEnd, 10),
        prizeIndex: parseInt(h?.prizeIndex, 10),
      };
    })
    .filter((h) => h.name);
};

const tensDigit = (prize) =>
  String(prize || '')
    .replace(/\D/g, '')
    .padStart(5, '0')
    .slice(-2, -1);
const unitsDigit = (prize) =>
  String(prize || '')
    .replace(/\D/g, '')
    .padStart(5, '0')
    .slice(-1);

const APPROXIMATION_LABEL = {
  exact: 'Número exato',
  same_series: 'Aproximação dentro da série',
  cross_series: 'Aproximação entre séries da casa',
};

const getAuthHeaders = () => ({
  Authorization: `${JSON.parse(localStorage.getItem(USER_TOKEN))}`,
});

function ReferralDashboard(props) {
  const classes = useStyles();
  const stationId = props.auth?.user?.establishments?.[0]?.stations?.[0]?.id;

  const [actions, setActions] = useState([]);
  const [selectedActionId, setSelectedActionId] = useState('');
  const [selectedHouse, setSelectedHouse] = useState('');
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [referralSearch, setReferralSearch] = useState('');
  const [referralResults, setReferralResults] = useState([]);
  const [referralSearchLoading, setReferralSearchLoading] = useState(false);
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [generating, setGenerating] = useState(false);
  const [generatedResult, setGeneratedResult] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  // Busca manual do indicado (CPF/e-mail) quando ele se cadastrou com e-mail
  // diferente do digitado na indicação (ex.: typo no funcional) e não casa auto.
  const [manualQuery, setManualQuery] = useState('');
  const [manualLoading, setManualLoading] = useState(false);

  const [reportsOpen, setReportsOpen] = useState(false);
  const [reportActionId, setReportActionId] = useState('');
  const [reportHouse, setReportHouse] = useState('');
  const [reportLoading, setReportLoading] = useState(false);
  const [reportType, setReportType] = useState('participants_period');
  const [reportDateStart, setReportDateStart] = useState('');
  const [reportDateEnd, setReportDateEnd] = useState('');
  const [reportDrawId, setReportDrawId] = useState('');
  const [reportDraws, setReportDraws] = useState([]);

  const [drawOpen, setDrawOpen] = useState(false);
  const [prizes, setPrizes] = useState(['', '', '', '', '']);
  const [drawType, setDrawType] = useState('weekly');
  const [drawLoading, setDrawLoading] = useState(false);
  const [drawResult, setDrawResult] = useState(null);
  const [previewWinners, setPreviewWinners] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  // Período de inscrição dos indicados (opcional): limita o sorteio aos números
  // gerados dentro do intervalo. Guardamos como objetos moment.
  const [drawStartDate, setDrawStartDate] = useState(null);
  const [drawEndDate, setDrawEndDate] = useState(null);

  const [historyOpen, setHistoryOpen] = useState(false);
  const [draws, setDraws] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [exportingDrawId, setExportingDrawId] = useState(null);
  const [drawEditId, setDrawEditId] = useState(null);
  const [deletingDrawId, setDeletingDrawId] = useState(null);

  const [reconcileOpen, setReconcileOpen] = useState(false);
  const [reconcileLoading, setReconcileLoading] = useState(false);
  const [reconcileResult, setReconcileResult] = useState(null);
  // Seleção de candidato + quantidade por indicação pendente, dentro do modal
  // "Verificar pendentes" (referralId -> { candidateId, quantity }).
  const [partialSelections, setPartialSelections] = useState({});
  const [partialGenerating, setPartialGenerating] = useState(null);

  const selectedAction = actions.find((a) => a.id === selectedActionId);
  const housesForSelectedAction = normalizeHouses(selectedAction?.houses);

  const fetchActions = useCallback(async () => {
    if (!stationId) return;
    try {
      const response = await axios.get(`${BASE_API}/referrals/dashboard/actions`, {
        headers: getAuthHeaders(),
        params: { station_id: stationId },
      });
      const list = Array.isArray(response.data) ? response.data : [];
      setActions(list);
      if (list.length > 0 && !selectedActionId) {
        setSelectedActionId(list[0].id);
      }
    } catch (error) {
      console.error('Erro ao buscar atividades:', error);
      toastr.error('Erro', 'Não foi possível carregar as atividades.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stationId]);

  const fetchReferrals = useCallback(async () => {
    if (!stationId) return;
    try {
      setLoading(true);
      const response = await axios.get(`${BASE_API}/referrals/dashboard/list`, {
        headers: getAuthHeaders(),
        params: {
          station_id: stationId,
          action_id: selectedActionId || undefined,
          house: selectedHouse || undefined,
          page,
          pageSize,
          search: search || undefined,
        },
      });
      setRows(response.data?.rows || []);
      setCount(response.data?.count || 0);
    } catch (error) {
      console.error('Erro ao buscar indicações:', error);
      toastr.error('Erro', 'Não foi possível carregar as indicações.');
      setRows([]);
      setCount(0);
    } finally {
      setLoading(false);
    }
  }, [stationId, selectedActionId, selectedHouse, page, pageSize, search]);

  useEffect(() => {
    fetchActions();
  }, [fetchActions]);

  useEffect(() => {
    fetchReferrals();
  }, [fetchReferrals]);

  useEffect(() => {
    if (!modalOpen || !selectedActionId) {
      setReferralResults([]);
      return undefined;
    }
    const timeout = setTimeout(async () => {
      try {
        setReferralSearchLoading(true);
        const response = await axios.get(`${BASE_API}/referrals/dashboard/search-referrals`, {
          headers: getAuthHeaders(),
          params: {
            station_id: stationId,
            action_id: selectedActionId,
            search: referralSearch,
            limit: 20,
          },
        });
        setReferralResults(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('Erro ao buscar indicações:', error);
        setReferralResults([]);
      } finally {
        setReferralSearchLoading(false);
      }
    }, 350);
    return () => clearTimeout(timeout);
  }, [referralSearch, modalOpen, selectedActionId, stationId]);

  const handleOpenModal = () => {
    setReferralSearch('');
    setReferralResults([]);
    setSelectedReferral(null);
    setQuantity(scheduleQuantityForCurrentMonth(selectedAction?.lucky_numbers_schedule));
    setGeneratedResult(null);
    setCandidates([]);
    setSelectedCandidate(null);
    setManualQuery('');
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    if (generating) return;
    setModalOpen(false);
  };

  // Quando o admin seleciona uma indicação que ainda NÃO tem invited_user_id,
  // busca candidatos (mesmo e-mail/CPF em qualquer station + match via e-mail
  // funcional do Sindilegis). Permite linkar e gerar num clique.
  useEffect(() => {
    if (!selectedReferral) {
      setCandidates([]);
      setSelectedCandidate(null);
      return;
    }
    if (selectedReferral.invited || selectedReferral.invited_user_id) {
      setCandidates([]);
      setSelectedCandidate(null);
      return;
    }
    let cancelled = false;
    setCandidatesLoading(true);
    setCandidates([]);
    setSelectedCandidate(null);
    setManualQuery('');
    axios
      .get(`${BASE_API}/referrals/dashboard/candidates/${selectedReferral.id}`, {
        headers: getAuthHeaders(),
      })
      .then((response) => {
        if (cancelled) return;
        setCandidates(Array.isArray(response.data?.candidates) ? response.data.candidates : []);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('Erro ao buscar candidatos:', error);
        setCandidates([]);
      })
      .finally(() => {
        if (!cancelled) setCandidatesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedReferral]);

  // Busca manual da conta do indicado por CPF/e-mail (quando não casou automático).
  const handleManualSearch = async () => {
    if (!selectedReferral) return;
    const q = manualQuery.trim();
    if (q.length < 3) {
      toastr.warning('Atenção', 'Informe o CPF ou e-mail do indicado para buscar.');
      return;
    }
    try {
      setManualLoading(true);
      const response = await axios.get(
        `${BASE_API}/referrals/dashboard/candidates/${selectedReferral.id}`,
        { headers: getAuthHeaders(), params: { q } }
      );
      const list = Array.isArray(response.data?.candidates) ? response.data.candidates : [];
      setCandidates(list);
      setSelectedCandidate(null);
      if (list.length === 0) {
        toastr.info('Nada encontrado', 'Nenhuma conta com esse CPF/e-mail.');
      }
    } catch (error) {
      console.error('Erro na busca manual:', error);
      toastr.error('Erro', 'Não foi possível buscar a conta.');
    } finally {
      setManualLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!selectedActionId) {
      toastr.warning('Atenção', 'Selecione uma atividade.');
      return;
    }
    if (!selectedReferral) {
      toastr.warning('Atenção', 'Selecione uma indicação.');
      return;
    }
    const needsLink = !selectedReferral.invited && !selectedReferral.invited_user_id;
    if (needsLink && !selectedCandidate) {
      toastr.warning('Atenção', 'Selecione a conta candidata do indicado para linkar.');
      return;
    }
    const qty = parseInt(quantity, 10);
    if (!qty || qty <= 0 || qty > 100) {
      toastr.warning('Atenção', 'Quantidade inválida (1 a 100).');
      return;
    }
    try {
      setGenerating(true);
      const response = await axios.post(
        `${BASE_API}/referrals/dashboard/generate-numbers`,
        {
          action_id: selectedActionId,
          referral_id: selectedReferral.id,
          quantity: qty,
          station_id: stationId,
          invited_user_id: needsLink ? selectedCandidate.id : undefined,
          manual_query: needsLink && manualQuery.trim() ? manualQuery.trim() : undefined,
        },
        { headers: getAuthHeaders() }
      );
      setGeneratedResult(response.data);
      toastr.success(
        'Sucesso',
        `${qty} número(s) gerado(s) e compartilhado(s) entre filiado e indicado.`
      );
      fetchReferrals();
    } catch (error) {
      const msg = error.response?.data?.error || 'Erro ao gerar números.';
      toastr.error('Erro', msg);
    } finally {
      setGenerating(false);
    }
  };

  const loadReportDraws = async (actionId) => {
    if (!actionId) {
      setReportDraws([]);
      return;
    }
    try {
      const response = await axios.get(`${BASE_API}/referrals/draws`, {
        headers: getAuthHeaders(),
        params: { action_id: actionId },
      });
      setReportDraws(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erro ao carregar sorteios do relatório:', error);
      setReportDraws([]);
    }
  };

  const handleOpenReports = () => {
    setReportActionId(selectedActionId || '');
    setReportHouse('');
    setReportType('participants_period');
    setReportDateStart('');
    setReportDateEnd('');
    setReportDrawId('');
    setReportDraws([]);
    if (selectedActionId) loadReportDraws(selectedActionId);
    setReportsOpen(true);
  };

  const handleCloseReports = () => {
    if (reportLoading) return;
    setReportsOpen(false);
  };

  const downloadSpaCsv = (rows, filename) => {
    const csv = [
      SPA_HEADERS.join(','),
      ...rows.map((r) => SPA_HEADERS.map((h) => csvEscape(r[h])).join(',')),
    ].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const reportActionSlug = () => {
    const name = actions.find((a) => a.id === parseInt(reportActionId, 10))?.name || 'atividade';
    return String(name).replace(/[^\w-]+/g, '_');
  };

  const handleDownloadDetailsCsv = async () => {
    const response = await axios.get(`${BASE_API}/referrals/dashboard/export`, {
      headers: getAuthHeaders(),
      params: {
        station_id: stationId,
        action_id: reportActionId,
        house: reportHouse || undefined,
      },
    });
    const data = response.data?.rows || [];
    if (data.length === 0) {
      toastr.info('Sem dados', 'Nenhuma indicação encontrada para os filtros.');
      return;
    }
    const headers = [
      'Atividade',
      'Casa',
      'Filiado',
      'CPF Filiado',
      'E-mail Filiado',
      'Indicado',
      'CPF Indicado',
      'E-mail Indicado',
      'Telefone Indicado',
      'Status',
      'Data da Indicação',
      'Data de Conclusão',
      'Números da Sorte',
    ];
    const rowsData = data.map((r) => [
      r.atividade,
      r.casa,
      r.filiado_nome,
      r.filiado_cpf,
      r.filiado_email,
      r.indicado_nome,
      r.indicado_cpf,
      r.indicado_email,
      r.indicado_telefone,
      r.status,
      r.data_indicacao ? new Date(r.data_indicacao).toLocaleString('pt-BR') : '',
      r.data_conclusao ? new Date(r.data_conclusao).toLocaleString('pt-BR') : '',
      r.numeros_da_sorte,
    ]);
    const csv = [
      headers.join(','),
      ...rowsData.map((row) => row.map((cell) => csvEscape(cell)).join(',')),
    ].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const houseSuffix = reportHouse ? `_${reportHouse}` : '';
    link.download = `indique_e_ganhe_${reportActionSlug()}${houseSuffix}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toastr.success('Sucesso', `Relatório gerado — ${rowsData.length} linha(s).`);
    setReportsOpen(false);
  };

  const handleDownloadParticipantsSpa = async (scope) => {
    if (scope === 'period' && (!reportDateStart || !reportDateEnd)) {
      toastr.warning('Atenção', 'Informe a data inicial e final do período.');
      return;
    }
    const params = {
      action_id: reportActionId,
      house: reportHouse || undefined,
      scope,
    };
    if (scope === 'period') {
      params.date_start = new Date(`${reportDateStart}T00:00:00`).toISOString();
      params.date_end = new Date(`${reportDateEnd}T23:59:59`).toISOString();
    }
    const response = await axios.get(`${BASE_API}/referrals/dashboard/export-participants-spa`, {
      headers: getAuthHeaders(),
      params,
    });
    const rows = response.data?.rows || [];
    if (rows.length === 0) {
      toastr.info('Sem dados', 'Nenhum participante encontrado para os filtros.');
      return;
    }
    const houseSuffix = reportHouse ? `_${reportHouse}` : '';
    const periodSuffix = scope === 'period' ? `_${reportDateStart}_a_${reportDateEnd}` : '_final';
    downloadSpaCsv(
      rows,
      `participantes_spa_${reportActionSlug()}${houseSuffix}${periodSuffix}.csv`
    );
    toastr.success('Sucesso', `Arquivo CSV (SPA) gerado — ${rows.length} linha(s).`);
    setReportsOpen(false);
  };

  const handleDownloadWinnersSpa = async () => {
    if (!reportDrawId) {
      toastr.warning('Atenção', 'Selecione o sorteio.');
      return;
    }
    const response = await axios.get(`${BASE_API}/referrals/draws/${reportDrawId}/export-spa`, {
      headers: getAuthHeaders(),
    });
    const rows = response.data?.rows || [];
    if (rows.length === 0) {
      toastr.info('Sem dados', 'Este sorteio não possui vencedores para exportar.');
      return;
    }
    downloadSpaCsv(rows, `vencedores_spa_sorteio_${reportDrawId}.csv`);
    toastr.success('Sucesso', `Arquivo CSV (SPA) gerado — ${rows.length} linha(s).`);
    setReportsOpen(false);
  };

  const handleDownloadReport = async () => {
    if (!reportActionId) {
      toastr.warning('Atenção', 'Selecione a atividade.');
      return;
    }
    try {
      setReportLoading(true);
      if (reportType === 'participants_period') {
        await handleDownloadParticipantsSpa('period');
      } else if (reportType === 'participants_final') {
        await handleDownloadParticipantsSpa('final');
      } else if (reportType === 'winners') {
        await handleDownloadWinnersSpa();
      } else {
        await handleDownloadDetailsCsv();
      }
    } catch (error) {
      console.error('Erro ao gerar relatório:', error);
      toastr.error('Erro', error.response?.data?.error || 'Não foi possível gerar o relatório.');
    } finally {
      setReportLoading(false);
    }
  };

  const finalDrawDateForSelected = selectedAction?.final_draw_date
    ? new Date(selectedAction.final_draw_date)
    : null;

  const handleOpenDraw = () => {
    if (!selectedActionId) {
      toastr.warning('Atenção', 'Selecione uma atividade.');
      return;
    }
    if (housesForSelectedAction.length === 0) {
      toastr.warning('Atenção', 'Configure as casas da atividade antes de realizar o sorteio.');
      return;
    }
    setPrizes(['', '', '', '', '']);
    setDrawResult(null);
    setDrawEditId(null);
    setDrawStartDate(null);
    setDrawEndDate(null);
    // Pré-seleciona 'final' se a data programada já passou ou é hoje
    const shouldPreselectFinal = finalDrawDateForSelected && new Date() >= finalDrawDateForSelected;
    setDrawType(shouldPreselectFinal ? 'final' : 'weekly');
    setDrawOpen(true);
  };

  const handleEditDraw = (draw) => {
    setDrawEditId(draw.id);
    setDrawResult(null);
    setPrizes(
      [draw.prize_1, draw.prize_2, draw.prize_3, draw.prize_4, draw.prize_5].map((p) =>
        String(p || '')
          .replace(/\D/g, '')
          .slice(0, 5)
      )
    );
    setDrawType(draw.draw_type === 'final' ? 'final' : 'weekly');
    setDrawStartDate(draw.period_start ? moment(draw.period_start) : null);
    setDrawEndDate(draw.period_end ? moment(draw.period_end) : null);
    setDrawOpen(true);
  };

  const handleCloseDraw = () => {
    if (drawLoading) return;
    setDrawOpen(false);
  };

  // Retorna lista de bases por casa, ou null se prêmios incompletos.
  const computePreview = () => {
    const allFilled = prizes.every((p) => /^\d{1,5}$/.test(p));
    if (!allFilled || housesForSelectedAction.length === 0) return null;
    const element = prizes.map((p) => unitsDigit(p)).join('');
    return housesForSelectedAction.map((h) => {
      const prize = prizes[h.prizeIndex - 1] || '0';
      const tens = parseInt(tensDigit(prize), 10);
      const rangeSize = h.seriesEnd - h.seriesStart + 1;
      const offset = tens % rangeSize;
      const baseSeries = padSeries(h.seriesStart + offset);
      return {
        houseName: h.name,
        baseSeries,
        baseLuckyNumber: `${baseSeries}.${element}`,
        prizeIndex: h.prizeIndex,
      };
    });
  };

  useEffect(() => {
    if (!drawOpen || drawResult) {
      setPreviewWinners(null);
      return undefined;
    }
    const allFilled = prizes.every((p) => /^\d{1,5}$/.test(p));
    if (!allFilled || !selectedActionId) {
      setPreviewWinners(null);
      return undefined;
    }
    let cancelled = false;
    setPreviewLoading(true);
    const timer = setTimeout(async () => {
      try {
        const response = await axios.post(
          `${BASE_API}/referrals/draws/preview`,
          {
            action_id: selectedActionId,
            prizes,
            draw_type: drawType,
            draw_id: drawEditId || undefined,
            period_start: drawStartDate ? drawStartDate.format('YYYY-MM-DD') : null,
            period_end: drawEndDate ? drawEndDate.format('YYYY-MM-DD') : null,
          },
          { headers: getAuthHeaders() }
        );
        if (!cancelled) setPreviewWinners(response.data?.winnersByHouse || []);
      } catch (error) {
        if (!cancelled) setPreviewWinners(null);
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    drawOpen,
    drawResult,
    prizes,
    drawType,
    selectedActionId,
    drawEditId,
    drawStartDate,
    drawEndDate,
  ]);

  const handleConfirmDraw = async () => {
    if (!prizes.every((p) => /^\d{1,5}$/.test(p))) {
      toastr.warning('Atenção', 'Preencha os 5 prêmios com até 5 dígitos cada.');
      return;
    }
    // Período é opcional, mas deve ser informado por completo (as duas datas) e
    // em ordem cronológica.
    if ((drawStartDate && !drawEndDate) || (!drawStartDate && drawEndDate)) {
      toastr.warning('Atenção', 'Preencha as duas datas do período ou deixe ambas em branco.');
      return;
    }
    if (drawStartDate && drawEndDate && drawStartDate.isAfter(drawEndDate, 'day')) {
      toastr.warning('Atenção', 'A data inicial não pode ser maior que a data final.');
      return;
    }
    try {
      setDrawLoading(true);
      const isEdit = !!drawEditId;
      const response = isEdit
        ? await axios.put(
            `${BASE_API}/referrals/draws/${drawEditId}`,
            { prizes, draw_type: drawType },
            { headers: getAuthHeaders() }
          )
        : await axios.post(
            `${BASE_API}/referrals/draws`,
            {
              action_id: selectedActionId,
              prizes,
              draw_type: drawType,
              period_start: drawStartDate ? drawStartDate.format('YYYY-MM-DD') : null,
              period_end: drawEndDate ? drawEndDate.format('YYYY-MM-DD') : null,
            },
            { headers: getAuthHeaders() }
          );
      setDrawResult(response.data);
      toastr.success(
        'Sucesso',
        isEdit ? 'Sorteio reapurado e ganhadores notificados.' : 'Sorteio realizado com sucesso.'
      );
      fetchReferrals();
      if (isEdit && historyOpen) refreshHistory();
    } catch (error) {
      const msg = error.response?.data?.error || 'Erro ao realizar sorteio.';
      toastr.error('Erro', msg);
    } finally {
      setDrawLoading(false);
    }
  };

  const refreshHistory = async () => {
    if (!selectedActionId) return;
    setHistoryLoading(true);
    try {
      const response = await axios.get(`${BASE_API}/referrals/draws`, {
        headers: getAuthHeaders(),
        params: { action_id: selectedActionId },
      });
      setDraws(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erro ao carregar sorteios:', error);
      setDraws([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleOpenHistory = async () => {
    if (!selectedActionId) {
      toastr.warning('Atenção', 'Selecione uma atividade.');
      return;
    }
    setHistoryOpen(true);
    await refreshHistory();
  };

  const handleDeleteDraw = async (drawId) => {
    const ok = window.confirm(
      'Excluir este sorteio? Os ganhadores deixarão de constar como premiados e voltam ao pool dos próximos sorteios. Esta ação não envia nenhum aviso aos participantes.'
    );
    if (!ok) return;
    setDeletingDrawId(drawId);
    try {
      await axios.delete(`${BASE_API}/referrals/draws/${drawId}`, {
        headers: getAuthHeaders(),
      });
      toastr.success('Sucesso', 'Sorteio excluído.');
      setDraws((prev) => prev.filter((d) => d.id !== drawId));
      fetchReferrals();
    } catch (error) {
      const msg = error.response?.data?.error || 'Erro ao excluir sorteio.';
      toastr.error('Erro', msg);
    } finally {
      setDeletingDrawId(null);
    }
  };

  const handleExportDrawSpa = async (drawId) => {
    setExportingDrawId(drawId);
    try {
      const response = await axios.get(`${BASE_API}/referrals/draws/${drawId}/export-spa`, {
        headers: getAuthHeaders(),
      });
      const rows = response.data?.rows || [];
      if (rows.length === 0) {
        toastr.info('Sem dados', 'Este sorteio não possui vencedores para exportar.');
        return;
      }
      const csv = [
        SPA_HEADERS.join(','),
        ...rows.map((r) => SPA_HEADERS.map((h) => csvEscape(r[h])).join(',')),
      ].join('\r\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `numeros_sorte_spa_sorteio_${drawId}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toastr.success('Sucesso', 'Arquivo CSV (SPA) gerado.');
    } catch (error) {
      console.error('Erro ao exportar CSV SPA:', error);
      toastr.error('Erro', error.response?.data?.error || 'Não foi possível exportar o CSV.');
    } finally {
      setExportingDrawId(null);
    }
  };

  const handleOpenReconcile = () => {
    setReconcileResult(null);
    setPartialSelections({});
    setReconcileOpen(true);
  };

  const handleCloseReconcile = () => {
    if (reconcileLoading) return;
    setReconcileOpen(false);
  };

  const handleReconcile = async () => {
    try {
      setReconcileLoading(true);
      const response = await axios.post(
        `${BASE_API}/referrals/dashboard/reconcile`,
        {
          station_id: stationId,
          action_id: selectedActionId || undefined,
        },
        { headers: getAuthHeaders() }
      );
      setReconcileResult(response.data);
      const generated = response.data?.numbersGenerated || 0;
      toastr.success(
        'Verificação concluída',
        generated > 0
          ? `${response.data.referralsCompleted} indicação(ões) concluída(s), ${generated} número(s) gerado(s).`
          : 'Nenhuma indicação pendente ficou elegível agora.'
      );
      fetchReferrals();
    } catch (error) {
      const msg = error.response?.data?.error || 'Erro ao verificar indicações.';
      toastr.error('Erro', msg);
    } finally {
      setReconcileLoading(false);
    }
  };

  // Gera os números direto da tela "Verificar pendentes", para uma indicação
  // que tem candidato identificado. Linka o candidato escolhido e gera a
  // quantidade do mês (o backend usa o lucky_numbers_schedule quando a
  // quantidade não é forçada; aqui mandamos a quantidade escolhida pelo gestor).
  const handleGeneratePartial = async (item) => {
    const selection = partialSelections[item.referralId] || {};
    const candidateId =
      selection.candidateId || (item.candidates?.length === 1 ? item.candidates[0].id : null);
    if (!candidateId) {
      toastr.warning('Atenção', 'Selecione a conta do indicado para gerar.');
      return;
    }
    const fallbackQty = scheduleQuantityForCurrentMonth(
      actions.find((a) => a.id === item.actionId)?.lucky_numbers_schedule
    );
    const rawQty =
      selection.quantity != null && selection.quantity !== '' ? selection.quantity : fallbackQty;
    const qty = parseInt(rawQty, 10);
    if (!qty || qty <= 0 || qty > 100) {
      toastr.warning('Atenção', 'Quantidade inválida (1 a 100).');
      return;
    }
    try {
      setPartialGenerating(item.referralId);
      const response = await axios.post(
        `${BASE_API}/referrals/dashboard/generate-numbers`,
        {
          action_id: item.actionId,
          referral_id: item.referralId,
          quantity: qty,
          station_id: stationId,
          invited_user_id: candidateId,
        },
        { headers: getAuthHeaders() }
      );
      const numbers = response.data?.numbers || [];
      toastr.success(
        'Sucesso',
        `${numbers.length} número(s) gerado(s) para ${
          response.data?.invited?.name || item.invitedName || 'o indicado'
        }.`
      );
      // Tira a indicação da lista de pendentes e move para "concluídas agora".
      setReconcileResult((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          partialMatches: Math.max(0, (prev.partialMatches || 0) - 1),
          referralsCompleted: (prev.referralsCompleted || 0) + 1,
          numbersGenerated: (prev.numbersGenerated || 0) + numbers.length,
          pendingCandidates: (prev.pendingCandidates || []).filter(
            (p) => p.referralId !== item.referralId
          ),
          completed: [
            {
              referralId: item.referralId,
              inviterName: response.data?.inviter?.name || item.inviterName || null,
              invitedName: response.data?.invited?.name || item.invitedName || null,
              invitedEmail: response.data?.invited?.email || item.invitedEmail || null,
              numbers,
            },
            ...(prev.completed || []),
          ],
        };
      });
      fetchReferrals();
    } catch (error) {
      const msg = error.response?.data?.error || 'Erro ao gerar números.';
      toastr.error('Erro', msg);
    } finally {
      setPartialGenerating(null);
    }
  };

  const renderPerson = (person, fallbackName, fallbackEmail, fallbackCpf) => {
    const name = person?.name || fallbackName || '—';
    const email = person?.email || fallbackEmail || '';
    const cpf = person?.cpf || fallbackCpf || '';
    return (
      <div className={classes.personBlock}>
        <span className={classes.personName}>{name}</span>
        {cpf && <span className={classes.personMeta}>CPF: {formatCpf(cpf)}</span>}
        {email && <span className={classes.personMeta}>{email}</span>}
      </div>
    );
  };

  return (
    <GeneralGrid container justifyContent="center" className={classes.container}>
      <GeneralGrid item xs={12}>
        <Box className={classes.header}>
          <Typography className={classes.title}>Indique e Ganhe</Typography>
          <Typography className={classes.subtitle}>
            Acompanhe indicações e gere números da sorte compartilhados para uma indicação
            específica (filiado e indicado recebem o mesmo número).
          </Typography>
        </Box>

        <Paper variant="outlined" className={classes.filtersCard}>
          <Box className={classes.filtersRow}>
            <TextField
              select
              label="Atividade"
              variant="outlined"
              size="small"
              value={selectedActionId}
              onChange={(e) => {
                setSelectedActionId(e.target.value);
                setSelectedHouse('');
                setPage(0);
              }}>
              <MenuItem value="">Todas as atividades</MenuItem>
              {actions.map((a) => (
                <MenuItem key={a.id} value={a.id}>
                  {a.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Casa"
              variant="outlined"
              size="small"
              value={selectedHouse}
              onChange={(e) => {
                setSelectedHouse(e.target.value);
                setPage(0);
              }}
              disabled={housesForSelectedAction.length === 0}>
              <MenuItem value="">Todas as casas</MenuItem>
              {housesForSelectedAction.map((h) => (
                <MenuItem key={h.name} value={h.name}>
                  {h.name}{' '}
                  <span style={{ color: '#888', marginLeft: 8, fontSize: 12 }}>
                    ({padSeries(h.seriesStart)}–{padSeries(h.seriesEnd)} · prêmio LF {h.prizeIndex})
                  </span>
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Buscar por nome ou CPF (filiado ou indicado)"
              variant="outlined"
              size="small"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box className={classes.actionsRow}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<EmojiEventsIcon />}
              className={classes.primaryActionBtn}
              onClick={handleOpenModal}
              disabled={!selectedActionId}>
              Gerar números para indicação
            </Button>

            <Button
              variant="outlined"
              color="primary"
              startIcon={<CasinoIcon />}
              className={classes.secondaryActionBtn}
              onClick={handleOpenDraw}
              disabled={!selectedActionId}>
              Adicionar números premiados
            </Button>

            <Button
              variant="outlined"
              color="primary"
              startIcon={<GetAppIcon />}
              className={classes.secondaryActionBtn}
              onClick={handleOpenReports}>
              Relatórios
            </Button>

            <Button
              variant="outlined"
              startIcon={<HistoryIcon />}
              className={classes.secondaryActionBtn}
              onClick={handleOpenHistory}
              disabled={!selectedActionId}>
              Histórico de sorteios
            </Button>

            <Button
              variant="outlined"
              color="primary"
              startIcon={<VerifiedUserIcon />}
              className={classes.secondaryActionBtn}
              onClick={handleOpenReconcile}>
              Verificar pendentes
            </Button>
          </Box>
        </Paper>

        <Paper variant="outlined" className={classes.tableCard}>
          {loading ? (
            <Box style={{ padding: 60, textAlign: 'center' }}>
              <LoadingProgress />
            </Box>
          ) : rows.length === 0 ? (
            <Box className={classes.emptyState}>
              <PersonAddIcon style={{ fontSize: 48, color: '#ccc' }} />
              <Typography variant="body1" style={{ marginTop: 8 }}>
                Nenhuma indicação encontrada.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.headCell}>Filiado (Indicador)</TableCell>
                    <TableCell className={classes.headCell}>Indicado</TableCell>
                    <TableCell className={classes.headCell}>WhatsApp</TableCell>
                    <TableCell className={classes.headCell}>Casa</TableCell>
                    <TableCell className={classes.headCell}>Atividade</TableCell>
                    <TableCell className={classes.headCell}>Status</TableCell>
                    <TableCell className={classes.headCell}>Data da indicação</TableCell>
                    <TableCell className={classes.headCell}>Números gerados</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r) => {
                    const status = statusConfig[r.status] || statusConfig.pending;
                    const allNumbers = Array.from(
                      new Set([...(r.inviter_numbers || []), ...(r.invited_numbers || [])])
                    );
                    return (
                      <TableRow key={r.id} hover>
                        <TableCell>{renderPerson(r.inviter, null, null, null)}</TableCell>
                        <TableCell>
                          {renderPerson(r.invited, r.invited_name, r.invited_email, r.invited_cpf)}
                        </TableCell>
                        <TableCell>{formatPhone(r.invited_phone)}</TableCell>
                        <TableCell>{r.house || '—'}</TableCell>
                        <TableCell>{r.action?.name || '—'}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={status.label}
                            className={classes.statusChip}
                            style={{
                              backgroundColor: `${status.color}22`,
                              color: status.color,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          {r.indication_date
                            ? new Date(r.indication_date).toLocaleString('pt-BR')
                            : '—'}
                        </TableCell>
                        <TableCell>
                          {allNumbers.length === 0 ? (
                            <span style={{ color: '#999' }}>—</span>
                          ) : (
                            <div className={classes.numbersWrap}>
                              {allNumbers.map((n) => (
                                <Chip
                                  key={n}
                                  size="small"
                                  label={n}
                                  className={classes.numberChip}
                                />
                              ))}
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <TablePagination
            component="div"
            count={count}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => {
              setPageSize(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Linhas por página"
            labelDisplayedRows={({ from, to, count: total }) => `${from}-${to} de ${total}`}
          />
        </Paper>

        <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
          <DialogTitle>Gerar números da sorte para indicação</DialogTitle>
          <DialogContent dividers>
            {generatedResult ? (
              <Box>
                <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                  Números gerados e compartilhados
                </Typography>
                <Typography variant="body2" style={{ color: '#666', marginBottom: 12 }}>
                  Filiado: {generatedResult.inviter?.name} (CPF{' '}
                  {formatCpf(generatedResult.inviter?.cpf)})
                  <br />
                  Indicado: {generatedResult.invited?.name} (CPF{' '}
                  {formatCpf(generatedResult.invited?.cpf)})
                </Typography>
                <Box>
                  {generatedResult.numbers?.map((n) => (
                    <span key={n} className={classes.numberBadge}>
                      {n}
                    </span>
                  ))}
                </Box>
              </Box>
            ) : (
              <Box>
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  label="Buscar pelo indicado (nome, e-mail ou CPF) ou nome do filiado"
                  value={referralSearch}
                  onChange={(e) => {
                    setReferralSearch(e.target.value);
                    setSelectedReferral(null);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: referralSearchLoading ? <CircularProgress size={16} /> : null,
                  }}
                  disabled={generating}
                  style={{ marginBottom: 12 }}
                />

                {selectedReferral ? (
                  <Paper variant="outlined" style={{ padding: 12, marginBottom: 12 }}>
                    <Typography variant="subtitle2" style={{ fontWeight: 700, color: '#1a3a5c' }}>
                      Indicação selecionada
                    </Typography>
                    <Typography variant="body2" style={{ marginTop: 4 }}>
                      <strong>Filiado:</strong> {selectedReferral.inviter?.name || '—'}
                      {selectedReferral.inviter?.cpf
                        ? ` · CPF ${formatCpf(selectedReferral.inviter.cpf)}`
                        : ''}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Indicado:</strong>{' '}
                      {selectedReferral.invited?.name || selectedReferral.invited_name || '—'}
                      {selectedReferral.invited?.cpf || selectedReferral.invited_cpf
                        ? ` · CPF ${formatCpf(
                            selectedReferral.invited?.cpf || selectedReferral.invited_cpf
                          )}`
                        : ''}
                    </Typography>
                    {!selectedReferral.invited && !selectedReferral.invited_user_id && (
                      <Box style={{ marginTop: 10 }}>
                        <Typography
                          variant="caption"
                          style={{ color: '#d32f2f', display: 'block', marginBottom: 6 }}>
                          Indicado não vinculado. Buscamos contas candidatas pelo e-mail/CPF
                          (qualquer station) e pelo e-mail oficial do Sindilegis. Escolha o correto
                          para linkar e gerar.
                        </Typography>

                        {/* Busca manual: para quando o indicado se cadastrou com e-mail
                            diferente do digitado na indicação (ex.: typo no funcional). */}
                        <Box style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                          <TextField
                            size="small"
                            variant="outlined"
                            fullWidth
                            label="Não achou? Buscar indicado por CPF ou e-mail"
                            value={manualQuery}
                            onChange={(e) => setManualQuery(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleManualSearch();
                              }
                            }}
                            disabled={generating || manualLoading}
                          />
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={handleManualSearch}
                            disabled={generating || manualLoading}
                            startIcon={
                              manualLoading ? (
                                <CircularProgress size={14} />
                              ) : (
                                <SearchIcon fontSize="small" />
                              )
                            }>
                            Buscar
                          </Button>
                        </Box>

                        {candidatesLoading ? (
                          <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <CircularProgress size={14} />
                            <Typography variant="caption" style={{ color: '#666' }}>
                              Buscando candidatos…
                            </Typography>
                          </Box>
                        ) : candidates.length === 0 ? (
                          <Typography variant="caption" style={{ color: '#888' }}>
                            Nenhuma conta candidata encontrada. O indicado ainda não tem cadastro
                            que dê pra linkar.
                          </Typography>
                        ) : (
                          <Box style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {candidates.map((c) => {
                              const isSelected = selectedCandidate?.id === c.id;
                              const reasonLabel = {
                                mesmo_email: 'mesmo e-mail',
                                mesmo_cpf: 'mesmo CPF',
                                email_oficial_sindilegis: 'e-mail oficial Sindilegis',
                                cpf_de_outro_candidato: 'mesmo CPF de outra conta',
                                busca_manual: 'busca manual',
                              };
                              return (
                                <Paper
                                  key={c.id}
                                  variant="outlined"
                                  onClick={() => setSelectedCandidate(c)}
                                  style={{
                                    padding: 10,
                                    cursor: 'pointer',
                                    borderColor: isSelected ? '#1a3a5c' : undefined,
                                    borderWidth: isSelected ? 2 : 1,
                                    backgroundColor: isSelected ? '#eef5ff' : '#fff',
                                  }}>
                                  <Typography
                                    variant="body2"
                                    style={{ fontWeight: 600, color: '#1a3a5c' }}>
                                    {c.name || '—'}{' '}
                                    {c.isCampaignStation && (
                                      <Chip
                                        size="small"
                                        label="station da campanha"
                                        style={{
                                          backgroundColor: '#c8e6c9',
                                          color: '#1b5e20',
                                          marginLeft: 6,
                                          height: 18,
                                        }}
                                      />
                                    )}
                                  </Typography>
                                  <Typography variant="caption" style={{ color: '#555' }}>
                                    Station {c.station_id} · CPF {c.cpf ? formatCpf(c.cpf) : '—'} ·{' '}
                                    {c.email || '—'}
                                  </Typography>
                                  <Box style={{ marginTop: 4 }}>
                                    {c.matchReasons.map((m) => (
                                      <Chip
                                        key={m}
                                        size="small"
                                        label={reasonLabel[m] || m}
                                        style={{
                                          marginRight: 4,
                                          height: 18,
                                          backgroundColor: '#eef5ff',
                                          color: '#1a3a5c',
                                        }}
                                      />
                                    ))}
                                    <Chip
                                      size="small"
                                      label={c.filiated && c.active ? 'filiado/ativo' : 'inativo'}
                                      style={{
                                        height: 18,
                                        backgroundColor:
                                          c.filiated && c.active ? '#e8f5e9' : '#ffebee',
                                        color: c.filiated && c.active ? '#2e7d32' : '#c62828',
                                      }}
                                    />
                                    {c.sindilegisFiliado === true && (
                                      <Chip
                                        size="small"
                                        label="Sindilegis: filiado ✓"
                                        style={{
                                          marginLeft: 4,
                                          height: 18,
                                          backgroundColor: '#2e7d32',
                                          color: '#fff',
                                        }}
                                      />
                                    )}
                                    {c.sindilegisFiliado === false && (
                                      <Chip
                                        size="small"
                                        label="Sindilegis: NÃO filiado"
                                        style={{
                                          marginLeft: 4,
                                          height: 18,
                                          backgroundColor: '#c62828',
                                          color: '#fff',
                                        }}
                                      />
                                    )}
                                  </Box>
                                </Paper>
                              );
                            })}
                          </Box>
                        )}
                      </Box>
                    )}
                    <Box style={{ marginTop: 10 }}>
                      <Button
                        size="small"
                        onClick={() => setSelectedReferral(null)}
                        disabled={generating}>
                        Trocar indicação
                      </Button>
                    </Box>
                  </Paper>
                ) : (
                  referralResults.length > 0 && (
                    <Paper
                      variant="outlined"
                      style={{ maxHeight: 260, overflowY: 'auto', marginBottom: 12 }}>
                      <List dense disablePadding>
                        {referralResults.map((r) => (
                          <React.Fragment key={r.id}>
                            <ListItem
                              button
                              className={classes.searchResultItem}
                              onClick={() => {
                                setSelectedReferral(r);
                                setReferralSearch('');
                                setReferralResults([]);
                              }}>
                              <ListItemText
                                primary={
                                  <span>
                                    <strong>{r.inviter?.name || '—'}</strong>
                                    {' → '}
                                    {r.invited?.name || r.invited_name || '—'}
                                  </span>
                                }
                                secondary={
                                  <span>
                                    Filiado: {r.inviter?.cpf ? formatCpf(r.inviter.cpf) : '—'} ·
                                    Indicado:{' '}
                                    {r.invited?.cpf || r.invited_cpf
                                      ? formatCpf(r.invited?.cpf || r.invited_cpf)
                                      : '—'}
                                  </span>
                                }
                              />
                            </ListItem>
                            <Divider />
                          </React.Fragment>
                        ))}
                      </List>
                    </Paper>
                  )
                )}

                {!selectedReferral && referralResults.length === 0 && !referralSearchLoading && (
                  <Typography
                    variant="caption"
                    style={{ display: 'block', color: '#888', marginBottom: 12 }}>
                    {referralSearch
                      ? 'Nenhuma indicação encontrada com esse termo.'
                      : 'Digite o nome ou CPF do filiado ou do indicado para buscar.'}
                  </Typography>
                )}

                <TextField
                  type="number"
                  label="Quantidade"
                  variant="outlined"
                  size="small"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  inputProps={{ min: 1, max: 100 }}
                  disabled={generating}
                  style={{ width: 180 }}
                />

                <MonthQuantityChips
                  schedule={selectedAction?.lucky_numbers_schedule}
                  value={quantity}
                  onPick={(q) => setQuantity(q)}
                  disabled={generating}
                />

                <Typography
                  variant="caption"
                  style={{
                    display: 'block',
                    marginTop: 12,
                    color: '#888',
                  }}>
                  O mesmo número será registrado para o filiado e para o indicado (compartilhado). A
                  unicidade é garantida pelo backend.
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            {generatedResult ? (
              <Button onClick={handleCloseModal} variant="contained" color="primary">
                Fechar
              </Button>
            ) : (
              <>
                <Button onClick={handleCloseModal} disabled={generating}>
                  Cancelar
                </Button>
                <Button
                  onClick={handleGenerate}
                  variant="contained"
                  color="primary"
                  disabled={
                    generating ||
                    !selectedReferral ||
                    (!selectedReferral.invited && !selectedCandidate)
                  }
                  startIcon={
                    generating ? (
                      <CircularProgress size={16} style={{ color: '#fff' }} />
                    ) : (
                      <EmojiEventsIcon />
                    )
                  }>
                  {generating ? 'Gerando...' : 'Gerar números'}
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>

        {/* Modal Relatórios */}
        <Dialog open={reportsOpen} onClose={handleCloseReports} maxWidth="sm" fullWidth>
          <DialogTitle>Gerar relatório</DialogTitle>
          <DialogContent dividers>
            <TextField
              select
              fullWidth
              size="small"
              variant="outlined"
              label="Tipo de relatório"
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}
              style={{ marginBottom: 16 }}>
              <MenuItem value="participants_period">Participantes por período (SPA)</MenuItem>
              <MenuItem value="participants_final">Participantes do sorteio final (SPA)</MenuItem>
              <MenuItem value="winners">Vencedores de um sorteio (SPA)</MenuItem>
              <MenuItem value="details_xlsx">Indicações detalhadas (CSV)</MenuItem>
            </TextField>
            <TextField
              select
              fullWidth
              size="small"
              variant="outlined"
              label="Atividade"
              value={reportActionId}
              onChange={(e) => {
                setReportActionId(e.target.value);
                setReportHouse('');
                setReportDrawId('');
                loadReportDraws(e.target.value);
              }}
              style={{ marginBottom: 16 }}>
              {actions.map((a) => (
                <MenuItem key={a.id} value={a.id}>
                  {a.name}
                </MenuItem>
              ))}
            </TextField>

            {reportType === 'participants_period' && (
              <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  type="date"
                  label="Data inicial"
                  InputLabelProps={{ shrink: true }}
                  value={reportDateStart}
                  onChange={(e) => setReportDateStart(e.target.value)}
                />
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  type="date"
                  label="Data final"
                  InputLabelProps={{ shrink: true }}
                  value={reportDateEnd}
                  onChange={(e) => setReportDateEnd(e.target.value)}
                />
              </div>
            )}

            {reportType === 'winners' && (
              <TextField
                select
                fullWidth
                size="small"
                variant="outlined"
                label="Sorteio"
                value={reportDrawId}
                onChange={(e) => setReportDrawId(e.target.value)}
                disabled={!reportActionId}
                style={{ marginBottom: 16 }}>
                {reportDraws.length === 0 && (
                  <MenuItem value="" disabled>
                    Nenhum sorteio encontrado
                  </MenuItem>
                )}
                {reportDraws.map((d) => (
                  <MenuItem key={d.id} value={d.id}>
                    {`#${d.id} · ${d.draw_type === 'final' ? 'Final' : 'Semanal'} · ${
                      d.draw_date ? new Date(d.draw_date).toLocaleDateString('pt-BR') : 's/ data'
                    }`}
                  </MenuItem>
                ))}
              </TextField>
            )}

            {reportType !== 'winners' && (
              <TextField
                select
                fullWidth
                size="small"
                variant="outlined"
                label="Casa (opcional)"
                value={reportHouse}
                onChange={(e) => setReportHouse(e.target.value)}
                disabled={!reportActionId}>
                <MenuItem value="">Todas as casas</MenuItem>
                {normalizeHouses(
                  actions.find((a) => a.id === parseInt(reportActionId, 10))?.houses
                ).map((h) => (
                  <MenuItem key={h.name} value={h.name}>
                    {h.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseReports} disabled={reportLoading}>
              Cancelar
            </Button>
            <Button
              onClick={handleDownloadReport}
              variant="contained"
              color="primary"
              disabled={reportLoading || !reportActionId}
              startIcon={
                reportLoading ? (
                  <CircularProgress size={16} style={{ color: '#fff' }} />
                ) : (
                  <GetAppIcon />
                )
              }>
              {reportLoading
                ? 'Gerando...'
                : reportType === 'details_xlsx'
                ? 'Baixar CSV'
                : 'Baixar CSV (SPA)'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Modal Sorteio */}
        <Dialog open={drawOpen} onClose={handleCloseDraw} maxWidth="sm" fullWidth>
          <DialogTitle>
            {drawEditId
              ? 'Editar sorteio (corrigir prêmios da Loteria Federal)'
              : 'Adicionar números premiados (Loteria Federal)'}
          </DialogTitle>
          <DialogContent dividers>
            {drawResult ? (
              <Box>
                <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                  Sorteio realizado ({drawResult.draw?.draw_type === 'final' ? 'Final' : 'Semanal'})
                </Typography>
                <Typography variant="body2" style={{ color: '#666', marginBottom: 12 }}>
                  Elemento base: <strong>{drawResult.base?.luckyElement || '—'}</strong>
                </Typography>
                {(drawResult.winnersByHouse || []).map((houseGroup) => (
                  <Paper
                    key={houseGroup.house}
                    variant="outlined"
                    style={{ padding: 12, marginBottom: 12 }}>
                    <Typography variant="subtitle2" style={{ fontWeight: 700, color: '#1a3a5c' }}>
                      Casa: {houseGroup.house}
                    </Typography>
                    <Typography variant="caption" style={{ color: '#666', display: 'block' }}>
                      Número base da casa:{' '}
                      <strong style={{ fontFamily: 'monospace' }}>
                        {houseGroup.baseLuckyNumber || '—'}
                      </strong>
                    </Typography>
                    {houseGroup.winners.length === 0 ? (
                      <Typography variant="caption" style={{ color: '#999' }}>
                        Sem ganhadores elegíveis nesta casa.
                      </Typography>
                    ) : (
                      houseGroup.winners.map((w) => (
                        <Box key={`${houseGroup.house}-${w.position}`} style={{ marginTop: 6 }}>
                          <Typography variant="body2">
                            {w.position}º —{' '}
                            <strong style={{ fontFamily: 'monospace' }}>{w.winningNumber}</strong>
                            <span style={{ color: '#666', marginLeft: 6 }}>
                              ({APPROXIMATION_LABEL[w.approximationKind] || 'Exato'}
                              {w.approximationKind === 'cross_series' && w.seriesAttempts > 1
                                ? ` · ${w.seriesAttempts} séries`
                                : ''}
                              )
                            </span>
                          </Typography>
                        </Box>
                      ))
                    )}
                  </Paper>
                ))}
              </Box>
            ) : (
              <Box>
                <Box style={{ marginBottom: 16 }}>
                  <FormLabel component="legend" style={{ fontSize: 14, fontWeight: 600 }}>
                    Tipo de sorteio
                  </FormLabel>
                  <RadioGroup row value={drawType} onChange={(e) => setDrawType(e.target.value)}>
                    <FormControlLabel
                      value="weekly"
                      control={<Radio color="primary" />}
                      label="Sorteio semanal"
                    />
                    <FormControlLabel
                      value="final"
                      control={<Radio color="primary" />}
                      label="Sorteio final"
                    />
                  </RadioGroup>
                  {finalDrawDateForSelected && (
                    <Typography variant="caption" style={{ display: 'block', color: '#666' }}>
                      ℹ Sorteio final programado para{' '}
                      {finalDrawDateForSelected.toLocaleString('pt-BR')}.
                      {new Date() >= finalDrawDateForSelected
                        ? ' Tipo "final" pré-selecionado automaticamente.'
                        : ''}
                    </Typography>
                  )}
                </Box>

                <Box style={{ marginBottom: 16 }}>
                  <FormLabel component="legend" style={{ fontSize: 14, fontWeight: 600 }}>
                    Período de inscrição dos indicados (opcional)
                  </FormLabel>
                  <Typography
                    variant="caption"
                    style={{ display: 'block', color: '#666', marginBottom: 8 }}>
                    Se preenchido, apenas os indicados que geraram números da sorte dentro do
                    intervalo concorrem neste sorteio. Em branco, considera todos.
                  </Typography>
                  <MuiPickersUtilsProvider utils={MomentUtils}>
                    <Box style={{ display: 'flex', gap: 12 }}>
                      <DatePicker
                        label="Data inicial"
                        value={drawStartDate}
                        onChange={setDrawStartDate}
                        format="DD/MM/YYYY"
                        inputVariant="outlined"
                        size="small"
                        autoOk
                        clearable
                        clearLabel="Limpar"
                        cancelLabel="Cancelar"
                        maxDate={drawEndDate || undefined}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                      />
                      <DatePicker
                        label="Data final"
                        value={drawEndDate}
                        onChange={setDrawEndDate}
                        format="DD/MM/YYYY"
                        inputVariant="outlined"
                        size="small"
                        autoOk
                        clearable
                        clearLabel="Limpar"
                        cancelLabel="Cancelar"
                        minDate={drawStartDate || undefined}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                      />
                    </Box>
                  </MuiPickersUtilsProvider>
                </Box>

                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
                  Informe os 5 prêmios da extração da Loteria Federal. O número base de cada casa
                  será calculado conforme a configuração (item 11.4 do regulamento).
                </Typography>
                {prizes.map((p, idx) => (
                  <TextField
                    key={idx}
                    label={`${idx + 1}º prêmio`}
                    value={p}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, '').slice(0, 5);
                      const next = [...prizes];
                      next[idx] = v;
                      setPrizes(next);
                    }}
                    inputProps={{ inputMode: 'numeric', maxLength: 5 }}
                    variant="outlined"
                    size="small"
                    fullWidth
                    style={{ marginBottom: 8 }}
                  />
                ))}
                {(() => {
                  const preview = computePreview();
                  if (!preview) return null;
                  return (
                    <Paper
                      variant="outlined"
                      style={{ padding: 12, marginTop: 12, backgroundColor: '#f5f7fa' }}>
                      <Typography
                        variant="caption"
                        style={{ color: '#666', display: 'block', marginBottom: 6 }}>
                        Números base por casa
                      </Typography>
                      {preview.map((p) => (
                        <Typography
                          key={p.houseName}
                          variant="body2"
                          style={{ fontFamily: 'monospace', color: '#1a3a5c' }}>
                          {p.houseName}: <strong>{p.baseLuckyNumber}</strong>{' '}
                          <span style={{ color: '#888', fontSize: 11 }}>
                            (prêmio LF {p.prizeIndex})
                          </span>
                        </Typography>
                      ))}
                    </Paper>
                  );
                })()}
                {(() => {
                  const allFilled = prizes.every((p) => /^\d{1,5}$/.test(p));
                  if (!allFilled) return null;
                  return (
                    <Paper
                      variant="outlined"
                      style={{ padding: 12, marginTop: 12, backgroundColor: '#f5faf5' }}>
                      <Typography
                        variant="caption"
                        style={{ color: '#666', display: 'block', marginBottom: 6 }}>
                        Ganhadores (prévia){previewLoading ? ' — calculando…' : ''}
                      </Typography>
                      {!previewLoading && previewWinners && previewWinners.length === 0 && (
                        <Typography variant="body2" style={{ color: '#888' }}>
                          Nenhum ganhador para estes prêmios.
                        </Typography>
                      )}
                      {(previewWinners || []).map((hg) => (
                        <div key={hg.house} style={{ marginBottom: 8 }}>
                          <Typography variant="body2" style={{ fontWeight: 600, color: '#1a3a5c' }}>
                            {hg.house}
                          </Typography>
                          {hg.winners.length === 0 ? (
                            <Typography variant="caption" style={{ color: '#888' }}>
                              — sem ganhador nesta casa
                            </Typography>
                          ) : (
                            hg.winners.map((w, i) => (
                              <Typography
                                key={i}
                                variant="body2"
                                style={{ fontFamily: 'monospace', fontSize: 12 }}>
                                <strong>{w.winningNumber}</strong> — {w.inviterName || '—'} /{' '}
                                {w.invitedName || '—'}{' '}
                                <span style={{ color: '#888' }}>
                                  (
                                  {w.approximationKind === 'exact'
                                    ? 'exato'
                                    : w.approximationKind === 'same_series'
                                    ? 'mesma série'
                                    : 'aproximação'}
                                  )
                                </span>
                              </Typography>
                            ))
                          )}
                        </div>
                      ))}
                    </Paper>
                  );
                })()}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            {drawResult ? (
              <Button onClick={handleCloseDraw} variant="contained" color="primary">
                Fechar
              </Button>
            ) : (
              <>
                <Button onClick={handleCloseDraw} disabled={drawLoading}>
                  Cancelar
                </Button>
                <Button
                  onClick={handleConfirmDraw}
                  variant="contained"
                  color="primary"
                  disabled={
                    drawLoading || !computePreview() || (computePreview() || []).length === 0
                  }
                  startIcon={
                    drawLoading ? (
                      <CircularProgress size={16} style={{ color: '#fff' }} />
                    ) : (
                      <CasinoIcon />
                    )
                  }>
                  {drawLoading ? 'Processando...' : drawEditId ? 'Reapurar sorteio' : 'Sortear'}
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>

        {/* Modal Histórico */}
        <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Histórico de sorteios</DialogTitle>
          <DialogContent dividers>
            {historyLoading ? (
              <Box style={{ textAlign: 'center', padding: 24 }}>
                <LoadingProgress />
              </Box>
            ) : draws.length === 0 ? (
              <Typography variant="body2" style={{ color: '#999' }}>
                Nenhum sorteio realizado para esta atividade.
              </Typography>
            ) : (
              draws.map((draw) => {
                const basesByHouse = draw.base_series_by_house_parsed || {};
                return (
                  <Paper key={draw.id} variant="outlined" style={{ padding: 12, marginBottom: 12 }}>
                    <Box
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}>
                      <Typography variant="subtitle2" style={{ fontWeight: 700, color: '#1a3a5c' }}>
                        {new Date(draw.draw_date).toLocaleString('pt-BR')}
                      </Typography>
                      <Box style={{ display: 'flex', alignItems: 'center' }}>
                        <Chip
                          size="small"
                          label={draw.draw_type === 'final' ? 'Sorteio final' : 'Sorteio semanal'}
                          style={{
                            backgroundColor: draw.draw_type === 'final' ? '#ad1457' : '#1976d2',
                            color: '#fff',
                            fontWeight: 600,
                            marginRight: 8,
                          }}
                        />
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={
                            exportingDrawId === draw.id ? (
                              <CircularProgress size={14} />
                            ) : (
                              <GetAppIcon />
                            )
                          }
                          disabled={
                            exportingDrawId === draw.id || !(draw.winners && draw.winners.length)
                          }
                          onClick={() => handleExportDrawSpa(draw.id)}>
                          {exportingDrawId === draw.id ? 'Gerando...' : 'Exportar CSV (SPA)'}
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          style={{ marginLeft: 8 }}
                          onClick={() => handleEditDraw(draw)}>
                          Editar
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          style={{ marginLeft: 8, color: '#c62828', borderColor: '#c62828' }}
                          disabled={deletingDrawId === draw.id}
                          onClick={() => handleDeleteDraw(draw.id)}>
                          {deletingDrawId === draw.id ? 'Excluindo...' : 'Excluir'}
                        </Button>
                      </Box>
                    </Box>
                    <Typography
                      variant="caption"
                      style={{ color: '#666', display: 'block', marginTop: 4 }}>
                      Prêmios LF:{' '}
                      {[draw.prize_1, draw.prize_2, draw.prize_3, draw.prize_4, draw.prize_5].join(
                        ' / '
                      )}
                    </Typography>
                    <Typography variant="caption" style={{ color: '#666', display: 'block' }}>
                      Elemento base: <strong>{draw.base_lucky_element}</strong>
                    </Typography>
                    {(draw.period_start || draw.period_end) && (
                      <Typography variant="caption" style={{ color: '#666', display: 'block' }}>
                        Período de inscrição:{' '}
                        <strong>
                          {draw.period_start ? moment(draw.period_start).format('DD/MM/YYYY') : '—'}{' '}
                          a {draw.period_end ? moment(draw.period_end).format('DD/MM/YYYY') : '—'}
                        </strong>
                      </Typography>
                    )}
                    {Object.keys(basesByHouse).length > 0 && (
                      <Box style={{ marginTop: 6 }}>
                        <Typography variant="caption" style={{ color: '#666', fontWeight: 600 }}>
                          Séries base por casa:
                        </Typography>
                        {Object.entries(basesByHouse).map(([house, series]) => (
                          <Typography
                            key={house}
                            variant="caption"
                            style={{ display: 'block', marginLeft: 8, fontFamily: 'monospace' }}>
                            {house}: {series}.{draw.base_lucky_element}
                          </Typography>
                        ))}
                      </Box>
                    )}
                    {(draw.winners || [])
                      .reduce((acc, w) => {
                        const found = acc.find((g) => g.house === w.house);
                        if (found) found.list.push(w);
                        else acc.push({ house: w.house, list: [w] });
                        return acc;
                      }, [])
                      .map((g) => (
                        <Box key={g.house} style={{ marginTop: 8 }}>
                          <Typography variant="body2" style={{ fontWeight: 600 }}>
                            Casa: {g.house}
                          </Typography>
                          {g.list
                            .sort((a, b) => a.position - b.position)
                            .map((w) => (
                              <Typography
                                key={w.id}
                                variant="caption"
                                style={{ display: 'block', marginLeft: 8 }}>
                                {w.position}º —{' '}
                                <span style={{ fontFamily: 'monospace' }}>{w.winning_number}</span>{' '}
                                <span style={{ color: '#666' }}>
                                  ({APPROXIMATION_LABEL[w.approximation_kind] || 'Exato'}
                                  {w.approximation_kind === 'cross_series' && w.series_attempts > 1
                                    ? ` · ${w.series_attempts} séries`
                                    : ''}
                                  )
                                </span>{' '}
                                — {w.referral?.inviter?.name || '—'} →{' '}
                                {w.referral?.invited?.name || w.referral?.invited_name || '—'}
                              </Typography>
                            ))}
                        </Box>
                      ))}
                  </Paper>
                );
              })
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setHistoryOpen(false)}>Fechar</Button>
          </DialogActions>
        </Dialog>

        {/* Modal Verificar pendentes */}
        <Dialog open={reconcileOpen} onClose={handleCloseReconcile} maxWidth="md" fullWidth>
          <DialogTitle>Verificar indicações pendentes</DialogTitle>
          <DialogContent dividers>
            {reconcileResult ? (
              <Box style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 8 }}>
                  Verificação concluída
                </Typography>
                {[
                  ['Indicações pendentes analisadas', reconcileResult.scannedPending],
                  ['Indicações concluídas agora', reconcileResult.referralsCompleted],
                  ['Números da sorte gerados', reconcileResult.numbersGenerated],
                  ['Ainda não cadastrados/filiados', reconcileResult.notRegistered],
                  ['Com candidato para revisão manual', reconcileResult.partialMatches || 0],
                  ['Já possuíam números', reconcileResult.alreadyHadNumbers],
                ].map(([label, value]) => (
                  <Box key={label} style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" style={{ color: '#555' }}>
                      {label}
                    </Typography>
                    <Typography variant="body2" style={{ fontWeight: 700, color: '#1a3a5c' }}>
                      {value}
                    </Typography>
                  </Box>
                ))}
                {/* Concluídas agora: quem ganhou números nesta verificação */}
                {reconcileResult.completed?.length > 0 && (
                  <Box style={{ marginTop: 14 }}>
                    <Typography
                      variant="subtitle2"
                      style={{ fontWeight: 700, color: '#2e7d32', marginBottom: 6 }}>
                      Concluídas agora ({reconcileResult.completed.length})
                    </Typography>
                    <Box style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {reconcileResult.completed.map((c) => (
                        <Paper
                          key={c.referralId}
                          variant="outlined"
                          style={{ padding: 10, backgroundColor: '#f4fbf5' }}>
                          <Typography variant="body2" style={{ fontWeight: 600, color: '#1a3a5c' }}>
                            {c.invitedName || '—'}{' '}
                            <span style={{ fontWeight: 400, color: '#777' }}>
                              {c.invitedEmail ? `· ${c.invitedEmail}` : ''}
                            </span>
                          </Typography>
                          <Typography variant="caption" style={{ color: '#555' }}>
                            Indicado por <strong>{c.inviterName || '—'}</strong>
                          </Typography>
                          <Box style={{ marginTop: 4 }}>
                            {(c.numbers || []).map((n) => (
                              <span key={n} className={classes.numberBadge}>
                                {n}
                              </span>
                            ))}
                          </Box>
                        </Paper>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Pendentes com candidato: revisar e gerar direto daqui */}
                {reconcileResult.pendingCandidates?.length > 0 && (
                  <Box style={{ marginTop: 16 }}>
                    <Typography
                      variant="subtitle2"
                      style={{ fontWeight: 700, color: '#1a3a5c', marginBottom: 4 }}>
                      Indicações encontradas para revisar (
                      {reconcileResult.pendingCandidates.length})
                    </Typography>
                    <Typography
                      variant="caption"
                      style={{ display: 'block', color: '#888', marginBottom: 8 }}>
                      Têm uma conta candidata identificada (mesmo e-mail/CPF em outra station ou
                      e-mail oficial do Sindilegis). Confirme o indicado correto e gere os números
                      sem sair desta tela.
                    </Typography>
                    <Box style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {reconcileResult.pendingCandidates.map((item) => {
                        const selection = partialSelections[item.referralId] || {};
                        const selectedCandidateId =
                          selection.candidateId ||
                          (item.candidates?.length === 1 ? item.candidates[0].id : null);
                        const isGenerating = partialGenerating === item.referralId;
                        const itemSchedule = actions.find(
                          (a) => a.id === item.actionId
                        )?.lucky_numbers_schedule;
                        const itemDefaultQty = scheduleQuantityForCurrentMonth(itemSchedule);
                        return (
                          <Paper key={item.referralId} variant="outlined" style={{ padding: 12 }}>
                            <Typography
                              variant="body2"
                              style={{ fontWeight: 600, color: '#1a3a5c' }}>
                              {item.invitedName || '—'}{' '}
                              <span style={{ fontWeight: 400, color: '#777' }}>
                                {item.invitedEmail ? `· ${item.invitedEmail}` : ''}
                              </span>
                            </Typography>
                            <Typography variant="caption" style={{ color: '#555' }}>
                              Indicado por <strong>{item.inviterName || '—'}</strong>
                              {item.inviterCpf ? ` · CPF ${formatCpf(item.inviterCpf)}` : ''}
                            </Typography>

                            <Typography
                              variant="caption"
                              style={{
                                display: 'block',
                                color: '#888',
                                marginTop: 8,
                                marginBottom: 4,
                              }}>
                              Conta candidata do indicado:
                            </Typography>
                            <Box style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                              {item.candidates.map((c) => {
                                const isSelected = selectedCandidateId === c.id;
                                const reasonLabel = {
                                  mesmo_email: 'mesmo e-mail',
                                  mesmo_cpf: 'mesmo CPF',
                                  email_oficial_sindilegis: 'e-mail oficial Sindilegis',
                                  cpf_de_outro_candidato: 'mesmo CPF de outra conta',
                                };
                                return (
                                  <Paper
                                    key={c.id}
                                    variant="outlined"
                                    onClick={() =>
                                      setPartialSelections((prev) => ({
                                        ...prev,
                                        [item.referralId]: {
                                          ...prev[item.referralId],
                                          candidateId: c.id,
                                        },
                                      }))
                                    }
                                    style={{
                                      padding: 10,
                                      cursor: 'pointer',
                                      borderColor: isSelected ? '#1a3a5c' : undefined,
                                      borderWidth: isSelected ? 2 : 1,
                                      backgroundColor: isSelected ? '#eef5ff' : '#fff',
                                    }}>
                                    <Typography
                                      variant="body2"
                                      style={{ fontWeight: 600, color: '#1a3a5c' }}>
                                      {c.name || '—'}{' '}
                                      {c.isCampaignStation && (
                                        <Chip
                                          size="small"
                                          label="station da campanha"
                                          style={{
                                            backgroundColor: '#c8e6c9',
                                            color: '#1b5e20',
                                            marginLeft: 6,
                                            height: 18,
                                          }}
                                        />
                                      )}
                                    </Typography>
                                    <Typography variant="caption" style={{ color: '#555' }}>
                                      Station {c.station_id} · CPF {c.cpf ? formatCpf(c.cpf) : '—'}{' '}
                                      · {c.email || '—'}
                                    </Typography>
                                    <Box style={{ marginTop: 4 }}>
                                      {(c.matchReasons || []).map((m) => (
                                        <Chip
                                          key={m}
                                          size="small"
                                          label={reasonLabel[m] || m}
                                          style={{
                                            marginRight: 4,
                                            height: 18,
                                            backgroundColor: '#eef5ff',
                                            color: '#1a3a5c',
                                          }}
                                        />
                                      ))}
                                      <Chip
                                        size="small"
                                        label={c.filiated && c.active ? 'filiado/ativo' : 'inativo'}
                                        style={{
                                          height: 18,
                                          backgroundColor:
                                            c.filiated && c.active ? '#e8f5e9' : '#ffebee',
                                          color: c.filiated && c.active ? '#2e7d32' : '#c62828',
                                        }}
                                      />
                                    </Box>
                                  </Paper>
                                );
                              })}
                            </Box>

                            <MonthQuantityChips
                              schedule={itemSchedule}
                              value={selection.quantity ?? itemDefaultQty}
                              onPick={(q) =>
                                setPartialSelections((prev) => ({
                                  ...prev,
                                  [item.referralId]: {
                                    ...prev[item.referralId],
                                    quantity: q,
                                  },
                                }))
                              }
                              disabled={isGenerating}
                            />

                            <Box
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                marginTop: 10,
                              }}>
                              <TextField
                                type="number"
                                size="small"
                                variant="outlined"
                                label="Qtd."
                                value={selection.quantity ?? itemDefaultQty}
                                onChange={(e) =>
                                  setPartialSelections((prev) => ({
                                    ...prev,
                                    [item.referralId]: {
                                      ...prev[item.referralId],
                                      quantity: e.target.value,
                                    },
                                  }))
                                }
                                inputProps={{ min: 1, max: 100 }}
                                style={{ width: 90 }}
                                disabled={isGenerating}
                              />
                              <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                disabled={isGenerating || !selectedCandidateId}
                                startIcon={
                                  isGenerating ? (
                                    <CircularProgress size={14} style={{ color: '#fff' }} />
                                  ) : (
                                    <EmojiEventsIcon />
                                  )
                                }
                                onClick={() => handleGeneratePartial(item)}>
                                {isGenerating ? 'Gerando...' : 'Gerar números'}
                              </Button>
                            </Box>
                          </Paper>
                        );
                      })}
                    </Box>
                  </Box>
                )}

                {reconcileResult.partialMatches > 0 &&
                  !(reconcileResult.pendingCandidates?.length > 0) && (
                    <Typography
                      variant="caption"
                      style={{ display: 'block', marginTop: 10, color: '#1a3a5c' }}>
                      <strong>{reconcileResult.partialMatches}</strong> indicação(ões) têm conta
                      candidata, mas não foi possível carregar os candidatos aqui. Abra{' '}
                      <em>Gerar números para indicação</em>, busque pela pessoa e gere por lá.
                    </Typography>
                  )}
              </Box>
            ) : (
              <Box>
                <Typography variant="body2" style={{ color: '#555' }}>
                  Reprocessa as indicações pendentes{' '}
                  {selectedAction ? (
                    <>
                      da atividade <strong>{selectedAction.name}</strong>
                    </>
                  ) : (
                    <>
                      de <strong>todas as atividades</strong>
                    </>
                  )}{' '}
                  e gera os números da sorte para quem já se cadastrou e filiou, mas ficou pendente
                  (ex.: e-mail informado diferente do cadastro). A validação por CPF e pelos e-mails
                  oficiais é a mesma do fluxo automático.
                </Typography>
                <Typography
                  variant="caption"
                  style={{ display: 'block', marginTop: 10, color: '#888' }}>
                  Operação segura de repetir: indicações que já têm números não são alteradas.
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            {reconcileResult ? (
              <Button onClick={handleCloseReconcile} variant="contained" color="primary">
                Fechar
              </Button>
            ) : (
              <>
                <Button onClick={handleCloseReconcile} disabled={reconcileLoading}>
                  Cancelar
                </Button>
                <Button
                  onClick={handleReconcile}
                  variant="contained"
                  color="primary"
                  disabled={reconcileLoading}
                  startIcon={
                    reconcileLoading ? (
                      <CircularProgress size={16} style={{ color: '#fff' }} />
                    ) : (
                      <VerifiedUserIcon />
                    )
                  }>
                  {reconcileLoading ? 'Verificando...' : 'Verificar agora'}
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>
      </GeneralGrid>
    </GeneralGrid>
  );
}

ReferralDashboard.propTypes = {
  auth: PropTypes.object,
};

const mapStateToProps = (state) => ({ auth: state.auth });
const mapDispatchToProps = (dispatch) => bindActionCreators({}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(ReferralDashboard);
